import BlockIcon from "@mui/icons-material/Block";
import CachedIcon from "@mui/icons-material/Cached";
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandableTable from "components/ExpandableTable";
import {
  LazyTableRow,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import SelectEntity from "components/SelectEntity";
import useGlobalContext from "hooks/globalData";
import { useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  IJobDataSummary,
  JobDataSummary,
  ListRequestOfJobDataSummaryFilter,
  ListResponseOfImportJobDataStatus,
  ListResponseOfJobDataSummary,
  ResponseCode,
} from "services/KnowfullyAdminClient";
import persistanceStore from "stores/persistanceStore";
import { JobDataStatus } from "types/common-types";
import GeneratedIdCell from "./GeneratedIdCell";
import ImportStatusChip from "./ImportStatusChip";
import { parseGeneratedId } from "./importEntityLinks";
import { ImportsSummaryMessages } from "./ImportsSummaryMessages";

interface IImportsSummary extends IJobDataSummary, LazyTableRow {
  id: string;
}

// Matches the summary page's cadence so the tiles above this table never disagree with it.
const POLL_INTERVAL_MS = 5000;

const StatusCell = ({ value }: { value: IImportsSummary }) => (
  <ImportStatusChip status={value.jobDataStatus} />
);

const GeneratedIdColumnCell = ({ value }: { value: IImportsSummary }) => (
  <GeneratedIdCell
    jobDataType={value.jobDataType}
    generatedId={parseGeneratedId(value.details)}
  />
);

const hasRecordsInProgress = (records: JobDataSummary[]) =>
  records.some(
    (r) =>
      r.jobDataStatus === JobDataStatus.Unprocessed ||
      r.jobDataStatus === JobDataStatus.Queued ||
      r.jobDataStatus === JobDataStatus.Processing ||
      r.jobDataStatus === JobDataStatus.Retrying
  );

/** One row per imported record — the JobData tier. This is the shape a CSV upload produces. */
export default function ImportsRecordSummaryTable({
  jobId,
}: Readonly<{ jobId: string }>) {
  const { migrationJobService } = useGlobalContext();
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("rowNumber");
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [summary, setSummary] = useState<IImportsSummary[]>([]);
  const [jobDataIdCancelled, setJobDataIdCancelled] = useState<
    string | undefined
  >(undefined);

  const jobDataStatusId = persistanceStore.useState((s) => s.jobDataStatusId);

  const jobDataStatusesResponse = useQuery<ListResponseOfImportJobDataStatus>(
    "importJobDataStatuses",
    () => migrationJobService.getJobDataStatuses(null)
  );

  const { data: jobDataSummaries, isLoading } = useQuery<
    ListResponseOfJobDataSummary
  >(
    [
      "importJobDataSummary",
      jobId,
      jobDataStatusId,
      page,
      rowsPerPage,
      orderBy,
      order,
    ],
    () =>
      migrationJobService.getJobDataSummaries(
        jobId,
        ListRequestOfJobDataSummaryFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: {
            jobDataStatusId,
          },
        })
      ),
    {
      refetchIntervalInBackground: true,
      refetchInterval: (latest) =>
        hasRecordsInProgress(latest?.data ?? []) ? POLL_INTERVAL_MS : false,
    }
  );

  const refresh = () =>
    queryClient.invalidateQueries(["importJobDataSummary", jobId]);

  const onCancelJobData = async (jobDataId: string) => {
    setJobDataIdCancelled(jobDataId);

    const response = await migrationJobService.cancelJobData(jobDataId);

    if (response.responseCode === ResponseCode.OK) {
      queryClient.invalidateQueries(["importJobDataSummary", jobId]);
      queryClient.invalidateQueries(["importJobProcessSnapshot", jobId]);
      queryClient.invalidateQueries(["importJobSummary", jobId]);
    }

    setJobDataIdCancelled(undefined);
  };

  const CancelButton = ({ value }: { value: IImportsSummary }) => (
    <Box textAlign="center">
      {jobDataIdCancelled === value.id ? (
        <CircularProgress size={26} />
      ) : (
        <IconButton
          size="small"
          disabled={
            value.jobDataStatus === JobDataStatus.Cancelled ||
            value.jobDataStatus === JobDataStatus.Processed ||
            value.jobDataStatus === JobDataStatus.Error ||
            value.jobDataStatus === JobDataStatus.Processing ||
            value.jobDataStatus === JobDataStatus.Retrying
          }
          onClick={() => onCancelJobData(value.id!)}
        >
          <BlockIcon />
        </IconButton>
      )}
    </Box>
  );

  const cells: Array<TableCellType> = [
    {
      accessor: "rowNumber",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "rowNumberLabel" }),
    },
    {
      accessor: "jobDataType",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "typeLabel" }),
    },
    {
      accessor: "jobDataStatus",
      align: "left",
      width: 0,
      type: "node",
      View: StatusCell,
      label: t({ id: "statusLabel" }),
    },
    {
      accessor: "generatedId",
      noSort: true,
      align: "left",
      width: 0,
      type: "node",
      View: GeneratedIdColumnCell,
      label: t({ id: "generatedIdLabel" }),
    },
    {
      accessor: "message",
      noSort: true,
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "messageLabel" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: CancelButton,
      label: "",
      noSort: true,
    },
  ];

  useEffect(() => {
    const rawSummary = (jobDataSummaries?.data ?? []).map(
      (row: JobDataSummary) => ({
        ...row,
        id: row.id!,
        isExpandable: row.details?.length! > 0,
        renderExpandedData: () => <ImportsSummaryMessages summary={row} />,
      })
    );

    setSummary(rawSummary);
  }, [jobDataSummaries?.data]);

  return (
    <>
      <Box mb={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={12} md={4}>
            <SelectEntity
              fetchedData={jobDataStatusesResponse}
              labelField="name"
              value={jobDataStatusId || ""}
              label={t({ id: "statusLabel" })}
              onChange={(id?: string) =>
                persistanceStore.update((s) => {
                  s.jobDataStatusId = id;
                })
              }
              textFieldProps={{ variant: "outlined" }}
              data-cy="jobDataStatusFilter"
            />
          </Grid>
        </Grid>
      </Box>
      <ExpandableTable
        {...{
          rowCount: jobDataSummaries?.totalRecords ?? 0,
          rows: summary ?? [],
          loading: isLoading,
          dense: true,
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          titleComponent: (
            <Box m={2}>
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
              >
                <Grid item xs={12} sm={10} md={10}>
                  <Typography variant="h6">
                    <T id="importsSummary" />
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={2} md={2}>
                  <Tooltip title={t({ id: "refresh" })}>
                    <IconButton onClick={refresh}>
                      <CachedIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </Box>
          ),
          collapsable: true,
        }}
      />
    </>
  );
}
